/**
 * Office ST SSO dialog page — opened via displayDialogAsync from the taskpane.
 * Redirects to PingFederate via GET /st/login?return_to=office, then returns Famla JWT via messageParent.
 */
import React, { useEffect, useState } from 'react';

import { config } from '../../../services/config';
import type { OfficeStAuthDialogResult } from './openOfficeStAuthDialog';

const OFFICE_JS_URL = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

function postResultToParent(result: OfficeStAuthDialogResult): void {
    const officeUi = (Office as any)?.context?.ui;
    if (officeUi && typeof officeUi.messageParent === 'function') {
        officeUi.messageParent(JSON.stringify(result));
        return;
    }

    if (process.env.REACT_APP_DEBUG === 'true') {
        // eslint-disable-next-line no-console
        console.warn('[OfficeStAuthDialog] messageParent unavailable', result);
    }
}

async function waitForOfficeJs(): Promise<void> {
    if (typeof Office !== 'undefined') {
        if (typeof (Office as any).onReady === 'function') {
            await (Office as any).onReady();
        }
        return;
    }

    await new Promise<void>((resolve, reject) => {
        const existing = document.getElementById('office-js') as HTMLScriptElement | null;
        const script = existing || document.createElement('script');
        if (!existing) {
            script.id = 'office-js';
            script.src = OFFICE_JS_URL;
            script.async = true;
            document.head.appendChild(script);
        }
        script.onload = () => {
            void (Office as any).onReady?.().then(resolve).catch(reject);
        };
        script.onerror = () => reject(new Error('Office.js failed to load'));
    });
}

const OfficeStAuthDialogEntry: React.FC = () => {
    const [message, setMessage] = useState('Connexion SSO ST…');

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            try {
                await waitForOfficeJs();
                if (cancelled) return;

                const params = new URLSearchParams(window.location.search);
                const accessToken = params.get('access_token');
                const refreshToken = params.get('refresh_token');
                const oauthError = params.get('error');
                const oauthErrorDescription = params.get('error_description');

                if (oauthError) {
                    postResultToParent({
                        status: 'error',
                        code: oauthError,
                        message: oauthErrorDescription || oauthError,
                    });
                    if (!cancelled) {
                        setMessage(oauthErrorDescription || oauthError);
                    }
                    return;
                }

                if (accessToken) {
                    postResultToParent({
                        status: 'success',
                        accessToken,
                        refreshToken: refreshToken || undefined,
                    });
                    return;
                }

                setMessage('Redirection vers SSO ST…');
                const apiUrl = config.api.baseUrl.replace(/\/+$/, '');
                window.location.assign(`${apiUrl}/st/login?return_to=office`);
            } catch (error) {
                const err = error as { message?: string };
                postResultToParent({
                    status: 'error',
                    message: err.message || 'ST SSO sign-in failed',
                });
                if (!cancelled) {
                    setMessage(err.message || 'Échec de la connexion');
                }
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div
            style={{
                fontFamily: 'Segoe UI, system-ui, sans-serif',
                padding: 24,
                textAlign: 'center',
                color: '#323130',
            }}
        >
            <p>{message}</p>
        </div>
    );
};

export default OfficeStAuthDialogEntry;
